import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { SignaturePad } from '../components/SignaturePad';
import { Loader2, CheckCircle2, FileText, Check, Shield, Star, CreditCard, Download, Phone, Mail, ChevronDown, Square, CheckSquare } from 'lucide-react';
import axios from 'axios';
import { invoicesAPI } from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt$ = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function EstimateApproval() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [selectedUpsellIds, setSelectedUpsellIds] = useState([]);
  const [approvalName, setApprovalName] = useState('');
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [approved, setApproved] = useState(false);
  const [depositPending, setDepositPending] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [payingInvoice, setPayingInvoice] = useState(false);
  const [expandedOption, setExpandedOption] = useState(null);
  const approvalRef = useRef(null);

  useEffect(() => {
    if (!approved || !token) return;
    const checkInvoice = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/estimates/public/${token}/invoice-status`);
        setInvoiceStatus(res.data);
      } catch { /* ok */ }
    };
    checkInvoice();
    const interval = setInterval(checkInvoice, 5000);
    return () => clearInterval(interval);
  }, [approved, token]);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/estimates/public/${token}`);
        setEstimate(res.data);
        const sel = res.data.options.find(o => o.is_selected);
        if (sel) setSelectedOptionId(sel.id);
        if (res.data.approved_upsell_ids?.length > 0) setSelectedUpsellIds(res.data.approved_upsell_ids);
        if (res.data.approved_at) setApproved(true);
      } catch (err) {
        setError(err?.response?.status === 404 ? 'This estimate link is invalid or has expired.' : 'Failed to load estimate.');
      } finally {
        setLoading(false);
      }
    };
    fetchEstimate();
  }, [token]);

  // Handle deposit return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('deposit_session');
    const canceled = searchParams.get('deposit_canceled');
    if (canceled) {
      setDepositPending(true);
      return;
    }
    if (!sessionId || !token) return;
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/estimates/public/${token}/confirm-deposit`);
        if (res.data.ok) {
          setApproved(true);
          setDepositPending(false);
        }
      } catch {
        setDepositPending(true);
      }
    })();
  }, [searchParams, token]);

  const handlePayInvoice = async () => {
    if (!invoiceStatus?.invoice_token) return;
    setPayingInvoice(true);
    try {
      const origin = window.location.origin;
      const res = await invoicesAPI.createCheckout(invoiceStatus.invoice_token, { origin_url: origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch {
      window.location.href = `${window.location.origin}/invoice/${invoiceStatus.invoice_token}`;
    }
  };

  const handleApprove = async () => {
    if (!approvalName.trim() || !signature) return;
    if (estimate.options.length > 0 && !selectedOptionId) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/estimates/public/${token}/approve`, {
        option_id: selectedOptionId || null,
        upsell_ids: selectedUpsellIds.length > 0 ? selectedUpsellIds : null,
        name: approvalName.trim(),
        signature,
      });
      if (res.data.deposit_required) {
        // Need to pay deposit — create Stripe checkout
        try {
          const checkoutRes = await axios.post(`${API_URL}/api/estimates/public/${token}/deposit-checkout`, {
            origin_url: window.location.origin,
          });
          if (checkoutRes.data.checkout_url) {
            window.location.href = checkoutRes.data.checkout_url;
            return;
          }
        } catch {
          // Stripe not available — show pending message
          setDepositPending(true);
        }
      } else {
        setApproved(true);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  // Separate maintenance plan upsells from regular upsells
  const maintenancePlanUpsell = estimate ? (estimate.upsells || []).find(u => u.maintenance_plan_id) : null;
  const regularUpsells = estimate ? (estimate.upsells || []).filter(u => !u.maintenance_plan_id) : [];

  // Maintenance plan price always included in total
  const maintenancePlanTotal = maintenancePlanUpsell ? maintenancePlanUpsell.price : 0;

  const computeTotal = () => {
    if (!estimate) return 0;
    const baseSubtotal = estimate.base_items.reduce((s, i) => s + i.total_price, 0);
    const selOpt = estimate.options.find(o => o.id === selectedOptionId);
    const optSubtotal = selOpt ? selOpt.subtotal : 0;
    const optTax = selOpt ? selOpt.tax_amount : 0;
    const upsellTotal = regularUpsells
      .filter(u => selectedUpsellIds.includes(u.id))
      .reduce((s, u) => s + u.price, 0);
    return baseSubtotal + optSubtotal + estimate.tax_amount + optTax + upsellTotal + maintenancePlanTotal - (estimate.discount_total || 0);
  };

  const computeDepositAmount = () => {
    if (!estimate || !estimate.deposit_type || !estimate.deposit_amount) return 0;
    if (estimate.deposit_type === 'fixed') return estimate.deposit_amount;
    if (estimate.deposit_type === 'percentage') return Math.round(computeTotal() * estimate.deposit_amount) / 100;
    return 0;
  };

  const hasDeposit = estimate?.deposit_type && estimate?.deposit_type !== 'none' && estimate?.deposit_amount > 0;

  const handleSelectOption = (optId) => {
    setSelectedOptionId(optId);
    setTimeout(() => approvalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
  };

  const toggleUpsell = (upsellId) => {
    setSelectedUpsellIds(prev =>
      prev.includes(upsellId) ? prev.filter(id => id !== upsellId) : [...prev, upsellId]
    );
  };

  const primaryColor = estimate?.theme?.primary || '#1e40af';

  // ── Loading ──
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  // ── Error ──
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center max-w-md px-6">
          <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-stone-900 mb-2">Estimate Not Found</h1>
          <p className="text-stone-500">{error}</p>
        </div>
      </div>
    );
  }

  // ── Expired ──
  if (estimate?.expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="estimate-expired">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-amber-500" />
          </div>
          <h1 className="text-xl font-bold text-stone-900 mb-2">This estimate has expired.</h1>
          <p className="text-stone-500">Please contact the office for an updated estimate.</p>
          {estimate.company_name && (
            <div className="mt-6 text-sm text-stone-500">
              <p className="font-medium text-stone-700">{estimate.company_name}</p>
              {estimate.company_phone && <p className="flex items-center gap-1 justify-center mt-1"><Phone className="w-3.5 h-3.5" />{estimate.company_phone}</p>}
              {estimate.company_email && <p className="flex items-center gap-1 justify-center mt-1"><Mail className="w-3.5 h-3.5" />{estimate.company_email}</p>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Deposit Pending State ──
  if (depositPending && !approved) {
    const depositAmt = computeDepositAmount();
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6 py-12" data-testid="deposit-pending">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-amber-50">
            <CreditCard className="w-10 h-10 text-amber-500" />
          </div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">Deposit Required</h1>
          <p className="text-stone-500 mb-6">
            Your approval has been recorded. A deposit of <span className="font-bold">{fmt$(depositAmt)}</span> is required to finalize.
          </p>
          <Button size="lg" className="px-8 gap-2 rounded-xl" onClick={async () => {
            try {
              const res = await axios.post(`${API_URL}/api/estimates/public/${token}/deposit-checkout`, {
                origin_url: window.location.origin,
              });
              if (res.data.checkout_url) window.location.href = res.data.checkout_url;
            } catch { alert('Could not start payment. Please contact the office.'); }
          }} style={{ backgroundColor: primaryColor }} data-testid="pay-deposit-btn">
            <CreditCard className="w-5 h-5" /> Pay {fmt$(depositAmt)} Deposit
          </Button>
          <p className="text-[10px] text-stone-400 mt-3">Secure payment powered by Stripe</p>
        </div>
      </div>
    );
  }

  // ── Approved State ──
  if (approved) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6 py-12" data-testid="approval-success">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${primaryColor}15` }}>
            <CheckCircle2 className="w-10 h-10" style={{ color: primaryColor }} />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-2">Estimate Approved</h1>
          <p className="text-stone-500 mb-6">
            {estimate.approval_name || approvalName} approved estimate <span className="font-semibold">{estimate.estimate_number}</span>
            {selectedOptionId && estimate.options.length > 0 && (
              <> — <span className="font-semibold">{estimate.options.find(o => o.id === selectedOptionId)?.name}</span> option</>
            )}
          </p>
          <div className="bg-white border rounded-2xl p-6 inline-block shadow-sm">
            <p className="text-sm text-stone-500 mb-1">Approved Total</p>
            <p className="text-4xl font-bold text-stone-900 tabular-nums" data-testid="approved-total">{fmt$(computeTotal())}</p>
          </div>

          {invoiceStatus?.has_invoice && !invoiceStatus?.invoice_paid && invoiceStatus?.stripe_connected && invoiceStatus?.invoice_status === 'sent' && (
            <div className="mt-8 bg-white border rounded-2xl p-6 shadow-sm" data-testid="approval-pay-now">
              <p className="text-sm text-stone-600 mb-4">Pay your invoice now to get started faster</p>
              <Button size="lg" className="px-8 gap-2 rounded-xl" onClick={handlePayInvoice} disabled={payingInvoice} data-testid="approval-pay-now-btn" style={{ backgroundColor: primaryColor }}>
                {payingInvoice ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> : <><CreditCard className="w-5 h-5" /> Pay {fmt$(invoiceStatus.invoice_total)} Now</>}
              </Button>
              <p className="text-[11px] text-stone-400 mt-3">Secure payment powered by Stripe</p>
            </div>
          )}
          {invoiceStatus?.has_invoice && invoiceStatus?.invoice_paid && (
            <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-4" data-testid="approval-already-paid">
              <CheckCircle2 className="w-6 h-6 text-green-600 mx-auto mb-1" />
              <p className="text-sm font-medium text-green-700">Invoice Paid</p>
            </div>
          )}
          {(estimate.signature_url || signature) && (
            <div className="mt-6" data-testid="approval-signature-display">
              <p className="text-xs text-stone-400 mb-2">Signature</p>
              <img src={estimate.signature_url ? `${API_URL}${estimate.signature_url}` : signature} alt="Signature" className="mx-auto border rounded-xl bg-white max-w-[280px]" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main Presentation ──
  const hasOptions = estimate.options.length > 0;
  const canApprove = approvalName.trim() && signature && (!hasOptions || selectedOptionId);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="estimate-approval-page">
      {/* ── Company Header ── */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {estimate.company_logo_url ? (
                <img src={estimate.company_logo_url} alt="" className="h-10 w-auto object-contain" data-testid="company-logo" />
              ) : (
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: primaryColor }} data-testid="company-logo-fallback">
                  {(estimate.company_name || 'C')[0]}
                </div>
              )}
              <div>
                <h2 className="text-base font-bold text-stone-900" data-testid="company-name">{estimate.company_name || 'Your Contractor'}</h2>
                <div className="flex items-center gap-3 text-xs text-stone-500">
                  {estimate.company_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{estimate.company_phone}</span>}
                  {estimate.company_email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{estimate.company_email}</span>}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg gap-1" onClick={async () => {
              try {
                const res = await axios.get(`${API_URL}/api/estimates/public/${token}/pdf`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                const a = document.createElement('a'); a.href = url; a.download = `${estimate.estimate_number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
              } catch { /* silent */ }
            }} data-testid="approval-download-pdf-btn">
              <Download className="w-3.5 h-3.5" /> PDF
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* ── Estimate Title ── */}
        <div className="text-center" data-testid="estimate-header">
          <p className="text-xs font-medium tracking-widest uppercase mb-2" style={{ color: primaryColor }}>Estimate {estimate.estimate_number}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-2" data-testid="approval-estimate-number">
            {estimate.estimate_name || `Estimate ${estimate.estimate_number}`}
          </h1>
          {estimate.customer_name && (
            <p className="text-base text-stone-500">Prepared for <span className="font-medium text-stone-700">{estimate.customer_name}</span></p>
          )}
          {estimate.description && <p className="text-sm text-stone-400 mt-2 max-w-lg mx-auto">{estimate.description}</p>}
        </div>

        {/* ── Base Items ── */}
        {estimate.base_items.length > 0 && (
          <Card className="rounded-2xl shadow-sm border-stone-200">
            <CardContent className="pt-5 pb-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">Included in All Options</h3>
              <div className="divide-y divide-stone-100">
                {estimate.base_items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2.5" data-testid={`base-item-${idx}`}>
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} />
                      <div>
                        <span className="text-sm font-medium text-stone-800">{item.name}</span>
                        {item.description && <p className="text-xs text-stone-400">{item.description}</p>}
                      </div>
                    </div>
                    <span className="text-sm font-medium text-stone-700 tabular-nums ml-4">{fmt$(item.total_price)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Option Cards (Good / Better / Best) ── */}
        {hasOptions && (
          <div className="space-y-5">
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-stone-900">Choose the solution that fits your needs</h2>
              <p className="text-sm text-stone-500 mt-1">Select the option that best fits your needs</p>
            </div>

            <div className={`grid gap-4 ${estimate.options.length === 2 ? 'sm:grid-cols-2 max-w-2xl mx-auto' : estimate.options.length >= 3 ? 'lg:grid-cols-3' : 'max-w-md mx-auto'}`}>
              {estimate.options.map((opt) => {
                const isSelected = selectedOptionId === opt.id;
                const isRec = opt.is_recommended;
                const isExpanded = expandedOption === opt.id;

                return (
                  <div
                    key={opt.id}
                    className={`relative rounded-2xl border-2 transition-all cursor-pointer flex flex-col overflow-hidden ${
                      isSelected
                        ? 'shadow-xl scale-[1.02]'
                        : isRec
                          ? 'shadow-lg hover:shadow-xl'
                          : 'shadow-sm hover:shadow-md'
                    }`}
                    style={{
                      borderColor: isSelected ? primaryColor : isRec ? primaryColor + '60' : '#e7e5e4',
                      ...(isSelected ? { boxShadow: `0 8px 30px ${primaryColor}20` } : {}),
                    }}
                    onClick={() => handleSelectOption(opt.id)}
                    data-testid={`approval-option-${opt.id}`}
                  >
                    {/* Most Popular banner */}
                    {isRec && (
                      <div className="text-white text-xs font-semibold text-center py-2" style={{ backgroundColor: primaryColor }} data-testid={`recommended-badge-${opt.id}`}>
                        <div className="flex items-center justify-center gap-1.5">
                          <Star className="w-3.5 h-3.5 fill-white" />
                          Most Popular
                        </div>
                      </div>
                    )}

                    {/* Media */}
                    {opt.media && opt.media.length > 0 && (
                      <div className="px-3 pt-3">
                        <div className={`grid gap-1.5 ${opt.media.length === 1 ? '' : 'grid-cols-2'}`}>
                          {opt.media.map((m) => (
                            m.media_type === 'video' ? (
                              <video key={m.id} src={m.file_url.startsWith('http') ? m.file_url : `${API_URL}${m.file_url}`} controls preload="metadata" className="w-full rounded-xl border" style={{ maxHeight: 160 }} data-testid={`approval-media-${m.id}`} />
                            ) : (
                              <img key={m.id} src={m.file_url.startsWith('http') ? m.file_url : `${API_URL}${m.file_url}`} alt="" className="w-full h-auto rounded-xl border object-cover" style={{ maxHeight: 160 }} data-testid={`approval-media-${m.id}`} />
                            )
                          ))}
                        </div>
                      </div>
                    )}
                    {opt.image_url && (!opt.media || opt.media.length === 0) && (
                      <div className="px-3 pt-3">
                        <img src={opt.image_url.startsWith('http') ? opt.image_url : `${API_URL}${opt.image_url}`} alt={opt.name} className="w-full h-32 object-cover rounded-xl" data-testid={`option-image-${opt.id}`} />
                      </div>
                    )}

                    {/* Card content */}
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="text-center mb-4">
                        <h3 className="text-lg font-bold text-stone-900" data-testid={`approval-option-name-${opt.id}`}>{opt.name}</h3>
                        {opt.description && <p className="text-xs text-stone-500 mt-1">{opt.description}</p>}
                      </div>

                      {/* Price */}
                      <div className="text-center mb-4">
                        <span className="text-3xl font-bold text-stone-900 tabular-nums" data-testid={`approval-option-total-${opt.id}`}>{fmt$(opt.total)}</span>
                        {opt.tax_amount > 0 && <p className="text-[10px] text-stone-400 mt-0.5">incl. {fmt$(opt.tax_amount)} tax</p>}
                      </div>

                      {/* Recommendation reason */}
                      {isRec && opt.recommendation_reason && (
                        <div className="text-xs text-center px-3 py-2 rounded-lg mb-3" style={{ backgroundColor: `${primaryColor}08`, color: primaryColor }} data-testid={`recommendation-reason-${opt.id}`}>
                          {opt.recommendation_reason}
                        </div>
                      )}

                      {/* Items list */}
                      {opt.items.length > 0 && (
                        <div className="flex-1 border-t border-stone-100 pt-3 mb-4">
                          <ul className="space-y-2">
                            {(isExpanded ? opt.items : opt.items.slice(0, 3)).map((item, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-xs" data-testid={`opt-item-${opt.id}-${idx}`}>
                                <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: primaryColor }} />
                                <div className="flex-1 min-w-0">
                                  <span className="text-stone-700 font-medium">{item.name}</span>
                                  {item.quantity > 1 && <span className="text-stone-400 ml-1">x{item.quantity}</span>}
                                  {item.description && <p className="text-[10px] text-stone-400 mt-0.5 line-clamp-1">{item.description}</p>}
                                </div>
                              </li>
                            ))}
                          </ul>
                          {opt.items.length > 3 && !isExpanded && (
                            <button className="text-[11px] mt-2 flex items-center gap-0.5 mx-auto" style={{ color: primaryColor }}
                              onClick={(e) => { e.stopPropagation(); setExpandedOption(opt.id); }}>
                              +{opt.items.length - 3} more <ChevronDown className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}

                      {/* Select button */}
                      <div
                        className={`mt-auto rounded-xl py-2.5 text-center text-sm font-semibold transition-all ${isSelected ? 'text-white' : 'text-stone-600'}`}
                        style={{ backgroundColor: isSelected ? primaryColor : '#f5f5f4' }}
                        data-testid={`option-select-indicator-${opt.id}`}
                      >
                        {isSelected ? (
                          <span className="flex items-center justify-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Selected</span>
                        ) : 'Select this option'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Included Maintenance Plan (non-optional) ── */}
        {maintenancePlanUpsell && (
          <Card className="rounded-2xl shadow-sm border-blue-200" data-testid="maintenance-plan-section">
            <CardContent className="pt-6 pb-5 px-5 sm:px-6">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: primaryColor + '12' }}>
                  <Shield className="w-4 h-4" style={{ color: primaryColor }} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-stone-800">Included Maintenance Plan</h3>
                  <p className="text-xs text-stone-400">This estimate includes the following maintenance plan</p>
                </div>
              </div>
              <div className="p-4 rounded-xl border-2 bg-blue-50/50 dark:bg-blue-950/20" style={{ borderColor: primaryColor + '30' }} data-testid="maintenance-plan-included">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-stone-800">{maintenancePlanUpsell.name}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold text-white" style={{ backgroundColor: primaryColor }}>INCLUDED</span>
                    </div>
                    {maintenancePlanUpsell.description && <p className="text-xs text-stone-500 mt-1 leading-relaxed">{maintenancePlanUpsell.description}</p>}
                  </div>
                  <span className="text-sm font-bold text-stone-700 tabular-nums flex-shrink-0 mt-0.5">{fmt$(maintenancePlanUpsell.price)}</span>
                </div>
                <p className="text-xs text-stone-400 mt-2 italic">By approving this estimate, you will be enrolled in this maintenance plan.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Upsells / Add-ons (optional, excludes maintenance plan) ── */}
        {regularUpsells.length > 0 && (
          <Card className="rounded-2xl shadow-sm border-stone-200" data-testid="upsells-section">
            <CardContent className="pt-6 pb-5 px-5 sm:px-6">
              <h3 className="text-sm font-bold text-stone-800 mb-0.5">Enhance Your Service</h3>
              <p className="text-xs text-stone-400 mb-5">Select any optional upgrades to improve performance, longevity, or protection</p>
              <div className="space-y-3">
                {regularUpsells.map(u => {
                  const isChecked = selectedUpsellIds.includes(u.id);
                  return (
                    <div
                      key={u.id}
                      className={`flex items-start gap-3.5 p-4 rounded-xl border-2 cursor-pointer transition-all min-h-[52px] ${isChecked ? 'border-stone-300 bg-stone-50' : 'border-stone-100 hover:border-stone-200'}`}
                      style={isChecked ? { borderColor: primaryColor + '50', backgroundColor: primaryColor + '06' } : {}}
                      onClick={() => toggleUpsell(u.id)}
                      data-testid={`upsell-option-${u.id}`}
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {isChecked ? (
                          <CheckSquare className="w-5 h-5" style={{ color: primaryColor }} />
                        ) : (
                          <Square className="w-5 h-5 text-stone-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-stone-800">{u.name}</span>
                        {u.description && <p className="text-xs text-stone-400 mt-0.5 leading-relaxed">{u.description}</p>}
                        {u.value_note && (
                          <p className="text-xs font-medium mt-1" style={{ color: primaryColor }}>{u.value_note}</p>
                        )}
                      </div>
                      <span className="text-sm font-bold text-stone-700 tabular-nums flex-shrink-0 mt-0.5">+{fmt$(u.price)}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Total Summary ── */}
        <Card className="rounded-2xl shadow-sm border-stone-200" data-testid="total-summary">
          <CardContent className="pt-6 pb-5 px-5 sm:px-6">
            {hasOptions && !selectedOptionId ? (
              <div className="text-center py-4" data-testid="select-option-prompt">
                <p className="text-sm text-stone-400 italic">Select an option above to see your total</p>
              </div>
            ) : (
              <div className="flex justify-end">
                <div className="w-full sm:w-80 space-y-2.5">
                  {estimate.base_items.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">Base Services</span>
                      <span className="tabular-nums font-medium text-stone-700">{fmt$(estimate.base_items.reduce((s, i) => s + i.total_price, 0))}</span>
                    </div>
                  )}
                  {hasOptions && selectedOptionId && (
                    <div className="flex justify-between text-sm">
                      <span className="text-stone-500">{estimate.options.find(o => o.id === selectedOptionId)?.name} Option</span>
                      <span className="tabular-nums font-medium text-stone-700">{fmt$(estimate.options.find(o => o.id === selectedOptionId)?.subtotal)}</span>
                    </div>
                  )}
                  {selectedUpsellIds.length > 0 && (
                    <>
                      <div className="border-t border-stone-100 pt-2" />
                      {regularUpsells.filter(u => selectedUpsellIds.includes(u.id)).map(u => (
                        <div key={u.id} className="flex justify-between text-sm">
                          <span className="text-stone-500">{u.name}</span>
                          <span className="tabular-nums font-medium text-stone-700">{fmt$(u.price)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {maintenancePlanUpsell && (
                    <>
                      <div className="border-t border-stone-100 pt-2" />
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500 flex items-center gap-1"><Shield className="w-3 h-3" /> {maintenancePlanUpsell.name}</span>
                        <span className="tabular-nums font-medium text-stone-700">{fmt$(maintenancePlanUpsell.price)}</span>
                      </div>
                    </>
                  )}
                  {(estimate.tax_amount > 0 || (selectedOptionId && estimate.options.find(o => o.id === selectedOptionId)?.tax_amount > 0)) && (
                    <>
                      <div className="border-t border-stone-100 pt-2" />
                      {estimate.discount_total > 0 && (
                        <div className="flex justify-between text-sm text-red-600">
                          <span>Discount{estimate.discount_type === 'percentage' ? ` (${estimate.discount_value}%)` : ''}</span>
                          <span className="tabular-nums font-medium">-{fmt$(estimate.discount_total)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-stone-500">Tax</span>
                        <span className="tabular-nums font-medium text-stone-700">{fmt$(estimate.tax_amount + (selectedOptionId ? (estimate.options.find(o => o.id === selectedOptionId)?.tax_amount || 0) : 0))}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t-2 border-stone-200 pt-3 mt-1">
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total</span>
                      <span className="tabular-nums" data-testid="approval-total">{fmt$(computeTotal())}</span>
                    </div>
                  </div>
                  {hasDeposit && (
                    <div className="rounded-lg px-3.5 py-2.5 mt-2" style={{ backgroundColor: primaryColor + '0C', border: `1px solid ${primaryColor}25` }}>
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-xs font-semibold" style={{ color: primaryColor }}>Deposit Due Today</span>
                          {estimate.deposit_type === 'percentage' && (
                            <span className="text-[10px] text-stone-400 ml-1">({estimate.deposit_amount}%)</span>
                          )}
                        </div>
                        <span className="text-base font-bold tabular-nums" style={{ color: primaryColor }} data-testid="deposit-amount">{fmt$(computeDepositAmount())}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Approval Form ── */}
        <div ref={approvalRef}>
          <Card className="rounded-2xl shadow-sm border-stone-200">
            <CardContent className="pt-6 pb-5">
              <h2 className="text-base font-bold text-stone-900 mb-1 flex items-center gap-2">
                <Shield className="w-4 h-4" style={{ color: primaryColor }} />
                Approve This Estimate
              </h2>
              <p className="text-xs text-stone-400 mb-5">Sign below to confirm the scope of work and pricing.</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-stone-600 block mb-1.5">Your Full Name</label>
                  <Input
                    value={approvalName}
                    onChange={(e) => setApprovalName(e.target.value)}
                    placeholder="Enter your full name to approve..."
                    className="text-sm rounded-xl h-11"
                    data-testid="approval-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-stone-600 block mb-2">Your Signature</label>
                  <SignaturePad onSignatureChange={setSignature} width={Math.min(420, window.innerWidth - 80)} height={150} />
                </div>
                {hasOptions && !selectedOptionId && (
                  <p className="text-xs text-amber-600 font-medium">Please select an option above before approving.</p>
                )}
                <Button
                  onClick={handleApprove}
                  disabled={!canApprove || submitting}
                  className="w-full h-14 text-base font-bold rounded-xl shadow-lg transition-all hover:shadow-xl active:scale-[0.98]"
                  style={{ backgroundColor: canApprove ? primaryColor : undefined }}
                  data-testid="approve-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : hasOptions && !selectedOptionId ? (
                    <>Select an option to approve</>
                  ) : hasDeposit ? (
                    <><CreditCard className="w-5 h-5 mr-2" /> Approve & Pay {fmt$(computeDepositAmount())} Deposit</>
                  ) : (
                    <><CheckCircle2 className="w-5 h-5 mr-2" /> Approve Estimate</>
                  )}
                </Button>
                <p className="text-[10px] text-stone-400 text-center">By signing and approving, you agree to the scope of work and pricing above.</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer className="border-t border-stone-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 text-center">
          <p className="text-xs text-stone-400">
            {estimate.company_name && <>{estimate.company_name} &middot; </>}
            {estimate.company_phone && <>{estimate.company_phone} &middot; </>}
            {estimate.company_email}
          </p>
        </div>
      </footer>
    </div>
  );
}
